import type { Ref } from "vue";
import { computed, onMounted, onUnmounted, ref, watch } from "vue";

import type { DiscussionThread } from "@/types";

import type { LayoutState, LayoutViewport } from "../types";

interface UseDiscussionThreadsLayoutOptions {
  selectedThread: Ref<DiscussionThread | null>;
  hasSelectedThreadInitially: boolean;
}

export function useDiscussionThreadsLayout({
  selectedThread,
  hasSelectedThreadInitially,
}: UseDiscussionThreadsLayoutOptions) {
  const detectViewport = (): LayoutViewport => {
    if (typeof window === "undefined") return "desktop";
    if (window.matchMedia("(min-width: 1024px)").matches) return "desktop";
    if (window.matchMedia("(min-width: 768px)").matches) return "tablet";
    return "mobile";
  };

  const viewport = ref<LayoutViewport>(detectViewport());
  const layoutState = ref<LayoutState>("sidebar");
  const showDetailTablet = ref(false);

  const syncViewport = () => {
    viewport.value = detectViewport();
  };

  const isMobileViewport = computed(() => viewport.value === "mobile");
  const isTabletViewport = computed(() => viewport.value === "tablet");
  const isDesktopViewport = computed(() => viewport.value === "desktop");
  const showDetailButton = computed(() => !isDesktopViewport.value);

  const showThreadPanel = () => {
    layoutState.value = "thread";
  };

  const showSidebarPanelState = () => {
    layoutState.value = "sidebar";
  };

  const closeTabletDetail = () => {
    showDetailTablet.value = false;
  };

  const resetWhenNoSelection = () => {
    closeTabletDetail();
    if (isMobileViewport.value) {
      showSidebarPanelState();
    }
  };

  onMounted(() => {
    if (typeof window === "undefined") return;
    window.addEventListener("resize", syncViewport);
  });

  onUnmounted(() => {
    if (typeof window === "undefined") return;
    window.removeEventListener("resize", syncViewport);
  });

  if (hasSelectedThreadInitially && isMobileViewport.value) {
    showThreadPanel();
  }

  watch(viewport, (incoming) => {
    if (incoming !== "mobile" && layoutState.value === "detail") {
      showThreadPanel();
    }

    if (incoming !== "tablet") {
      closeTabletDetail();
    }
  });

  const handleSelectedThreadChange = (
    incoming: DiscussionThread | null,
    previous: DiscussionThread | null,
  ) => {
    const isSameThread = previous && incoming && previous.id === incoming.id;
    if (isSameThread) return;

    if (incoming && isMobileViewport.value) {
      showThreadPanel();
      return;
    }

    if (!incoming) {
      resetWhenNoSelection();
    }
  };

  const handleSelectedThreadMissing = () => {
    resetWhenNoSelection();
  };

  const handleThreadClick = (event: MouseEvent, hasOrganization: boolean) => {
    if (!hasOrganization) {
      event.preventDefault();
      showThreadPanel();
      return;
    }

    showThreadPanel();
    if (isTabletViewport.value) {
      closeTabletDetail();
    }
  };

  const openDetailPanel = () => {
    if (!selectedThread.value) return;

    if (isMobileViewport.value) {
      layoutState.value = "detail";
      return;
    }

    if (isTabletViewport.value) {
      showDetailTablet.value = !showDetailTablet.value;
    }
  };

  const closeDetailPanel = () => {
    if (isMobileViewport.value) {
      showThreadPanel();
      return;
    }

    closeTabletDetail();
  };

  const showSidebarPanel = () => {
    showSidebarPanelState();
  };

  const sidebarPanelVisible = computed(() =>
    isMobileViewport.value ? layoutState.value === "sidebar" : true,
  );

  const threadPanelVisible = computed(() =>
    isMobileViewport.value ? layoutState.value === "thread" : true,
  );

  const detailPanelVisible = computed(() => {
    if (!selectedThread.value) return false;
    if (isMobileViewport.value) return layoutState.value === "detail";
    if (isDesktopViewport.value) return true;
    return showDetailTablet.value;
  });

  const detailCloseVariant = computed<"none" | "back" | "close">(() => {
    if (isMobileViewport.value) return "back";
    if (isTabletViewport.value) return "close";
    return "none";
  });

  const showTabletDetailOverlay = computed(
    () => isTabletViewport.value && detailPanelVisible.value,
  );

  const detailPanelClass = computed<string[]>(() => [
    "flex h-full min-h-0 flex-col overflow-hidden rounded-2xl border border-border bg-background",
    ...(isMobileViewport.value ? ["w-full"] : []),
    ...(isTabletViewport.value
      ? ["absolute inset-y-0 right-0 z-20 w-[320px]"]
      : []),
    ...(isDesktopViewport.value ? ["w-[300px] min-w-[300px]"] : []),
  ]);

  const detailCloseLabel = computed(() =>
    isMobileViewport.value ? "Volver al hilo" : "Cerrar detalles",
  );

  const chatVariant = computed<"desktop" | "mobile">(() =>
    isMobileViewport.value ? "mobile" : "desktop",
  );

  return {
    viewport,
    layoutState,
    showDetailTablet,
    isMobileViewport,
    isTabletViewport,
    isDesktopViewport,
    showDetailButton,
    handleSelectedThreadChange,
    handleSelectedThreadMissing,
    handleThreadClick,
    openDetailPanel,
    closeDetailPanel,
    showSidebarPanel,
    sidebarPanelVisible,
    threadPanelVisible,
    detailPanelVisible,
    detailCloseVariant,
    showTabletDetailOverlay,
    detailPanelClass,
    detailCloseLabel,
    chatVariant,
  };
}
